"use client";

import { useLocale } from "use-intl";

import { FallbackImage } from "@/components/fallback-image";
import { Link } from "@/components/link";
import type { RouterOutputs } from "@/types/rpc";
import { buildImageSrcSet, withImageRenderParams } from "@garimpa/lib/imageUrls";

import { artistResultCardClassName } from "./artist-result-card-class-name";
import { artistResultMetadata } from "./artist-result-metadata";
import { ArtistResultMetadataView } from "./artist-result-metadata-view";

type Artist = RouterOutputs["music"]["searchArtists"]["artists"][number];

const ARTIST_IMAGE_PRIORITY_COUNT = 6;

export function ArtistResultCard({
  artist,
  index,
  showAllArtists,
  formatStyleOrGenre,
}: {
  artist: Artist;
  index: number;
  showAllArtists: boolean;
  formatStyleOrGenre: (value: string) => string;
}) {
  const locale = useLocale();
  const metadata = artistResultMetadata(artist, locale, formatStyleOrGenre);
  return (
    <Link to={`/artist/${artist.id}`} className={artistResultCardClassName(index, showAllArtists)}>
      <div className="size-24 rounded-full border-2 border-transparent group-hover:border-primary transition-all p-1">
        <div className="relative w-full h-full rounded-full overflow-hidden bg-secondary">
          <FallbackImage
            src={withImageRenderParams(artist.imageUrl, { width: 150 })}
            srcSet={buildImageSrcSet(artist.imageUrl, [150, 320])}
            alt={artist.name}
            fallback="person"
            priority={index < ARTIST_IMAGE_PRIORITY_COUNT}
            className="rounded-full"
            sizes="96px"
          />
        </div>
      </div>
      <p className="font-bold text-sm text-center group-hover:text-primary transition-colors w-full line-clamp-2">
        {artist.name}
      </p>
      <ArtistResultMetadataView artist={artist} metadata={metadata} />
    </Link>
  );
}
